import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Link, useNavigate } from "@tanstack/react-router";
import { useState, type ReactElement } from "react";
import { Field, Modal, useToast } from "@cx/ui";
import type { CompanyCreated } from "@cx/contracts";
import { can, type Permissions } from "@cx/db";
import { ApiFailure } from "../../api";
import { errorMessage } from "../../errors";
import { useSession } from "../../session";

const CONSOLIDATED = "/meu-negocio/consolidado";

/**
 * Abas de empresa dos dashboards (RF-UI-06): troca a empresa ativa sem relogar (o mesmo do Empresa ▾ do header),
 * leva ao Consolidado (com mais de uma empresa) e, com `companies:create`, abre "+ Adicionar empresa". No Consolidado,
 * a aba de uma empresa volta para o Mensal dela.
 */
export function CompanyTabs({ consolidated = false }: { consolidated?: boolean }): ReactElement | null {
  const { me, companyId, setCompanyId } = useSession();
  const navigate = useNavigate();
  const [adding, setAdding] = useState(false);
  const canCreate = can(me.permissions as Permissions, "companies", "create");
  if (me.companies.length < 2 && !canCreate) return null;
  return (
    <nav aria-label="Empresas" className="mb-3">
      <ul className="nav nav-tabs flex-nowrap overflow-x-auto overflow-y-hidden">
        {me.companies.map((c) => {
          const active = !consolidated && c.id === companyId;
          return (
            <li key={c.id} className="nav-item">
              <button
                type="button"
                className={`nav-link text-nowrap${active ? " active" : ""}`}
                aria-current={active ? "page" : undefined}
                onClick={() => {
                  setCompanyId(c.id);
                  if (consolidated) void navigate({ to: "/meu-negocio/dashboard" });
                }}
              >
                {c.tradeName || c.legalName}
              </button>
            </li>
          );
        })}
        {me.companies.length > 1 && (
          <li className="nav-item">
            <Link
              to={CONSOLIDATED}
              className={`nav-link text-nowrap${consolidated ? " active" : ""}`}
              aria-current={consolidated ? "page" : undefined}
            >
              Consolidado
            </Link>
          </li>
        )}
        {canCreate && (
          <li className="nav-item ms-auto">
            <button type="button" className="nav-link text-nowrap" onClick={() => setAdding(true)}>
              + Adicionar empresa
            </button>
          </li>
        )}
      </ul>
      {adding && <AddCompanyDialog onClose={() => setAdding(false)} />}
    </nav>
  );
}

/** "+ Adicionar empresa": razão social, nome fantasia e CNPJ (opcional); o resto do perfil fica nas configurações. */
function AddCompanyDialog({ onClose }: { onClose(): void }): ReactElement {
  const { api } = useSession();
  const qc = useQueryClient();
  const toast = useToast();
  const [open, setOpen] = useState(true);
  const [key] = useState(() => crypto.randomUUID());
  const [v, setV] = useState({ legalName: "", tradeName: "", cnpj: "" });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const save = useMutation({
    mutationFn: () =>
      api<CompanyCreated>("/companies", {
        method: "POST",
        headers: { "Idempotency-Key": key },
        body: JSON.stringify({
          legalName: v.legalName,
          tradeName: v.tradeName || null,
          ...(v.cnpj.trim() ? { cnpj: v.cnpj } : {}),
        }),
      }),
    onSuccess: (c) => {
      toast("success", `Empresa ${c.tradeName || c.legalName} incluída`);
      void qc.invalidateQueries({ queryKey: ["me"] });
      setOpen(false);
    },
    onError: (e) => {
      if (e instanceof ApiFailure && e.fields) setErrors(e.fields);
      toast("danger", errorMessage(e));
    },
  });
  return (
    <Modal
      open={open}
      title="Adicionar empresa"
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn btn-outline-secondary" onClick={() => setOpen(false)}>
            Cancelar
          </button>
          <button
            type="button"
            className="btn btn-primary"
            disabled={save.isPending}
            onClick={() => save.mutate()}
          >
            Salvar
          </button>
        </>
      }
    >
      <div className="row">
        <Field label="Razão social" required className="col-12" error={errors.legalName}>
          <input
            data-autofocus=""
            value={v.legalName}
            maxLength={200}
            onChange={(e) => setV({ ...v, legalName: e.target.value })}
          />
        </Field>
        <Field label="Nome fantasia" className="col-md-6" error={errors.tradeName}>
          <input
            value={v.tradeName}
            maxLength={60}
            onChange={(e) => setV({ ...v, tradeName: e.target.value })}
          />
        </Field>
        <Field
          label="CNPJ"
          className="col-md-6"
          error={errors.cnpj}
          hint="Opcional: identifica as vendas entre as empresas"
        >
          <input
            value={v.cnpj}
            maxLength={20}
            inputMode="text"
            onChange={(e) => setV({ ...v, cnpj: e.target.value })}
          />
        </Field>
      </div>
      <p className="small text-muted mb-0">
        Endereço, inscrições, regime e certificado ficam nas configurações da empresa depois de incluída.
      </p>
    </Modal>
  );
}
